
<template>
  <div>
    <h1>Earns Methoden</h1>

    <p>
      Maßnahmen können mit der überschlägigen oder der genormten / wissenschaftlichen 
      Methode berechnet werden. Die überschlägigen Methoden benötigen weniger Daten 
      und sind somit schneller zu untersuchen. Die genormten / wissenschaftlichen Methoden 
      bieten auf der anderen Seite eine zuverlässige Berechnung welche belastbar sind.
    </p>

    <p>
      Um eine Maßnahme abspeichern zu können, sind in alle technischen
      Pflichtfelder valide Werte einzutragen. Zusätzlich können optional die
      finanziellen Parameter eingetragen werden.
    </p>

    <h1>Elektrische Antriebe (überschlägig)</h1>

    <ul>
      <li>
        Die Anzahl der Anlagen sollte nicht vereinfacht werden, da dies zur
        Effizienzsteigerung führt.
      </li>
    </ul>

    <v-expansion-panels accordion class="mt-5">

      <!-- PANEL 1 -->
      <v-expansion-panel>
        <v-expansion-panel-title>
          <div class="heading">Schritte vor der Berechnung</div>
        </v-expansion-panel-title>

        <v-expansion-panel-text>
          <ol>
            <li>
              <b>
                Einsparpotential und Wirtschaftlichkeit mit EARNS-Feinanalyse
                abschätzen anhand der Typenschilddaten
              </b>
              <p>Erste Annahmen hierbei:</p>
              <ul>
                <li>Der Bestands-Antrieb läuft im Auslegungspunkt gemäß Typenschild</li>
              </ul>
            </li>

            <li>
              <b>
                Kosten für die gewählte Systemlösung ermitteln, unter
                Berücksichtigung aller relevanten Faktoren wie Aufwand für Demontage,
                Umbau, Einbringung, Anschlussarbeiten, Inbetriebnahme, etc.
              </b>
            </li>

            <li><b>Angebotspreis für die gewählte Systemlösung kalkulieren</b></li>

            <li><b>Finale Einsparhöhe gemäß der gewählten Systemlösung berechnen</b></li>

            <li>
              <b>
                Finale Wirtschaftlichkeit (Einsparung, Rendite, Amortisation) anhand
                des Angebotspreises und der finalen Einsparhöhe ermitteln
              </b>
            </li>

          </ol>
        </v-expansion-panel-text>
      </v-expansion-panel>

      <!-- PANEL 2 -->
      <v-expansion-panel>
        <v-expansion-panel-title>
          <div class="heading">Eingabe der Bestandsanlage (Referenz) und der geplanten Anlage</div>
        </v-expansion-panel-title>

        <v-expansion-panel-text>
          <table>
            <caption>Parameter der Anlage</caption>
            <thead>
              <tr><th>Parameter</th><th>Beschreibung</th></tr>
            </thead>
            <tbody>
              <tr>
                <td>Anzahl</td>
                <td>Anzahl der elektrischen Antriebe des gleichen Typs und der gleichen Betriebsweise.</td>
              </tr>
              <tr>
                <td>Nennleistung</td>
                <td>Auswahl der auf dem Typenschild angegebenen Nennleistung in kW</td>
              </tr>
              <tr>
                <td>Baujahr</td>
                <td>Eingabe des Baujahrs (nächstmögliches verwenden, falls unbekannt)</td>
              </tr>
              <tr>
                <td>Bauart</td>
                <td>Auswahl der Elektromotorart</td>
              </tr>
              <tr>
                <td>VSD / FU</td>
                <td>Wählen Sie die vorhandene Regelungsmethode</td>
              </tr>
              <tr>
                <td>Jährliche Betriebsstunden</td>
                <td>Für 24/7 Betrieb: 8760 Stunden pro Jahr</td>
              </tr>
            </tbody>
          </table>

          <table>
            <caption>Finanzielle Parameter</caption>
            <thead>
              <tr><th>Parameter</th><th>Beschreibung</th></tr>
            </thead>
            <tbody>
              <tr>
                <td>Demontagekosten (Referenz)</td>
                <td>Arbeitszeit, Maschinenkosten, Entsorgung</td>
              </tr>
              <tr>
                <td>Kosten Neuanlage (Geplant)</td>
                <td>Anschaffung + Installation + Inbetriebnahme</td>
              </tr>
              <tr>
                <td>Betriebs- und Instandhaltungskosten</td>
                <td>Jährliche Kosten ohne Energiekosten</td>
              </tr>
            </tbody>
          </table>

        </v-expansion-panel-text>
      </v-expansion-panel>

      <!-- PANEL 3 -->
      <v-expansion-panel>
        <v-expansion-panel-title>
          <div class="heading">Berechnungsgrundlage und Daten</div>
        </v-expansion-panel-title>

        <v-expansion-panel-text eager>

          <h4>Alte Wirkungsgradklassen (EFF)</h4>
          <p>
            Im Rahmen der freiwilligen Selbstverpflichtung zwischen CEMEP und der EU
            wurden 1998 folgende Klassen im Bereich 1,1–90 kW definiert:
          </p>

          <table>
            <thead>
              <tr><th>Klasse</th><th>Beschreibung</th></tr>
            </thead>
            <tbody>
              <tr><td>EFF3</td><td>Niedriger Wirkungsgrad (bis ca. Jahr 2000)</td></tr>
              <tr><td>EFF2</td><td>Verbesserter Wirkungsgrad</td></tr>
              <tr><td>EFF1</td><td>Erhöhter Wirkungsgrad</td></tr>
            </tbody>
          </table>

          <h4>Neue Wirkungsgradklassen (IE) ab 2008</h4>
          <p>
            IEC 60034‑30:2008 definiert Motorwirkungsgradklassen 0,75–375 kW:
          </p>

          <table>
            <thead>
              <tr><th>Klasse</th><th>Beschreibung</th></tr>
            </thead>
            <tbody>
              <tr><td>IE1</td><td>Standard (≈ EFF2)</td></tr>
              <tr><td>IE2</td><td>Hoher Wirkungsgrad (≈ EFF1)</td></tr>
              <tr><td>IE3</td><td>Premium Efficiency</td></tr>
              <tr><td>IE4</td><td>Super Premium Efficiency</td></tr>
              <tr><td>IE5</td><td>Ultra Premium Efficiency</td></tr>
            </tbody>
          </table>

          <p>EU-Verordnungen:</p>
          <ul>
            <li>Seit 2021: IE3 Pflicht für 0.75–1000 kW</li>
            <li>Seit 2023: IE4 Pflicht für 75–200 kW, IE2 Pflicht für Einphasenmotoren</li>
          </ul>

          <img
            :src="powerEffImg"
            alt="Leistung und Effizienz"
          />

          <h4>EFF → IE Vergleich</h4>
          <p>
            Unterschied liegt im Messverfahren. Beispiel:
            4‑polig, 11 kW:
            EFF1 = 91.0% → identisch zu IE2 = 89.8%.
          </p>

          <img
            :src="effKlassenImg"
            alt="Effizienz Klassen"
          />

        </v-expansion-panel-text>
      </v-expansion-panel>

      <!-- PANEL 4 -->
      <v-expansion-panel>
        <v-expansion-panel-title>
          <div class="heading">Zusätzliche Informationen</div>
        </v-expansion-panel-title>

        <v-expansion-panel-text eager>
          <v-container>
            <h2>Elektrische Antriebe</h2>

            <p>
              Elektromotoren sind in Europa größte Stromverbraucher:
              65–70% in der Industrie, ein Drittel im Dienstleistungssektor.
              96% der Lebenszykluskosten eines Motors sind Energiekosten.
            </p>

            <h3>Optimierungsmöglichkeiten</h3>

            <table>
              <thead>
                <tr><th>Kategorie</th><th>Beschreibung</th></tr>
              </thead>
              <tbody>
                <tr><td>Motor</td><td>Austausch auf energieeffizienten IE3-Motor</td></tr>
                <tr><td>Dimensionierung</td><td>Pumpe/Motor auf realen Bedarf auslegen</td></tr>
                <tr><td>Regelung</td><td>Variable Speed Drive (VSD) einsetzen</td></tr>
                <tr><td>Betriebszeit</td><td>Betriebsdauer auf echte Nutzung anpassen</td></tr>
                <tr><td>Verbraucher</td><td>Mechanische Optimierung</td></tr>
              </tbody>
            </table>

          </v-container>
        </v-expansion-panel-text>
      </v-expansion-panel>

      <!-- PANEL 5 -->
      <v-expansion-panel>
        <v-expansion-panel-title>
          <div class="heading">Quellen</div>
        </v-expansion-panel-title>

        <v-expansion-panel-text>
          <h2>Quellen</h2>
          <ul>
            <li>ZVEI-Publikation „Motoren und geregelte Antriebe“</li>
            <li>ZVEI-Publikation „Energiesparen mit elektrischen Antrieben“</li>
            <li>Topmotors.ch</li>
            <li>Motor-Challenge-Programm der EU</li>
            <li>Hochschule Luzern, Technik & Architektur</li>
            <li>EU-Richtlinie 2005/32/EG und IEC 60034-30</li>
            <li>EU-Richtlinie 2019/1781</li>
            <li>Arqum GmbH / Forschungsstelle für Energiewirtschaft e.V. (2010)</li>
          </ul>
        </v-expansion-panel-text>
      </v-expansion-panel>

    </v-expansion-panels>

  </div>
</template>

<script setup lang="ts">
import effKlassenImg from '@/assets/help/electricdrive/eff-klassen.png'
import powerEffImg from '@/assets/help/electricdrive/power-eff.png'
</script>

<style scoped></style>
